import axios from 'axios';
import _ from 'lodash';
import i18n from 'i18n-js';

import qs from 'qs';
import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import * as Sentry from 'utils/sentry.js';

import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import { SERVICE_NAMES } from 'constants/ucd';

export const concedoAuthHeader = 'Authorization';

const CONCEDO_V2_ENABLED_COOKIE = 'concedo-v2-enabled';
const ASSIGN_ID_ENABLED_COOKIE = 'assign-id-enabled';
const concedoV2EnabledKey = 'concedov2enabled';
const assignIdEnabledKey = 'assignidenabled';

function shouldMockRequest(request) {
  // TODO: remove this last check after testing.
  return (
    window.Grab.config.MOCK_ALL_REQUESTS ||
    (process.env.NODE_ENV === 'development' && request.mock)
  );
}

let mockRequest = () => {};

if (process.env.NODE_ENV === 'development') {
  mockRequest = async (request) => {
    const mocks = require('../mocks').default;
    const mockRoutes = Object.keys(mocks);
    const { mock = {} } = request;
    const { fail = false, delay = 500, status = 500 } = mock;
    let requestUrl = `${request.method} ${request.url}`;

    await new Promise((resolve) => setTimeout(resolve, delay));

    if (fail) {
      requestUrl = `${requestUrl}#fail`;
    }

    for (const route of mockRoutes) {
      const matches = requestUrl.match(route);
      // eslint-disable-next-line no-continue
      if (!matches) continue;

      const id = _.toInteger(matches[1]);
      const responseData = _.isFunction(mocks[route])
        ? mocks[route](id)
        : mocks[route];

      return fail
        ? Promise.reject({
          status,
          message: responseData,
        })
        : Promise.resolve({ data: responseData });
    }
  };
}

function makeUpRequest(
  request,
  accessToken,
  baseUrl,
  authHeader,
  concedoAuthHeader,
  appendHedwigHeader = false,
) {
  const cookieDomain = window.location.host.replace(/omega[a-z-]*/, '');
  const queryValues = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  if (queryValues[concedoV2EnabledKey] === 'true') {
    Cookies.set(CONCEDO_V2_ENABLED_COOKIE, 'true', { domain: cookieDomain });
  }
  if (queryValues[assignIdEnabledKey] === 'true') {
    Cookies.set(ASSIGN_ID_ENABLED_COOKIE, 'true', { domain: cookieDomain });
  }

  const req = _.cloneDeep(request);
  req.url = `${baseUrl}${request.url}`;
  req.headers = {
    'Content-Type': 'application/json',
    'X-Request-ID': uuid(),
  };

  if (appendHedwigHeader) {
    req.headers['x-grabkit-clientid'] = 'ucd';
  }
  req.paramsSerializer = (params) =>
    qs.stringify(params, { arrayFormat: 'repeat' });

  if (!req.params) {
    req.params = {};
  }
  if (Cookies.get(CONCEDO_V2_ENABLED_COOKIE) === 'true') {
    req.params[concedoV2EnabledKey] = 'true';
  }
  if (Cookies.get(ASSIGN_ID_ENABLED_COOKIE) === 'true') {
    req.params[assignIdEnabledKey] = 'true';
  }

  if (accessToken) {
    req.headers[authHeader] = accessToken;
    req.headers[concedoAuthHeader] = `bearer ${accessToken}`;
  }

  return req;
}

function makeRequest(
  request,
  accessToken,
  baseUrl,
  authHeader,
  concedoAuthHeader,
  appendHedwigHeader = false,
) {
  if (shouldMockRequest(request)) {
    return mockRequest(request);
  }

  const req = makeUpRequest(
    request,
    accessToken,
    baseUrl,
    authHeader,
    concedoAuthHeader,
    appendHedwigHeader,
  );
  return axios(req);
}

function makeBatchRequests(
  requests,
  accessToken,
  baseUrl,
  authHeader,
  concedoAuthHeader,
) {
  const axiosRequests = requests.map((request) =>
    makeRequest(request, accessToken, baseUrl, authHeader, concedoAuthHeader),
  );
  return Promise.all(axiosRequests);
}

function showError(error, { checkNetwork } = {}) {
  const errorData = _.get(error, 'response.data.errors', []);
  const errorMessages = _.map(errorData, ({ code, data, message, target }) => {
    // Set prefix as target if target is present
    let prefix = '';
    if (target) {
      prefix = `${target}: `;
    }
    // Message priority: code error message from locales -> message
    // -> code (sentence cased) -> null (gets changed to something_went_wrong msg)
    const extractedMessage = i18n.t(`errors.${_.toLower(code)}`, {
      defaultValue: message || _.startCase(_.lowerCase(code)) || null,
      ...data,
    });
    return extractedMessage && prefix + extractedMessage;
  });

  let message;

  if (checkNetwork && error.code === 'ERR_NETWORK') {
    message =
      "Oops, something went wrong. Make sure you're connected to GrabVPN and try again.";
  } else if (!_.isEmpty(errorMessages)) {
    message = errorMessages;
  } else {
    message =
      _.get(error, 'response.statusText') ||
      _.get(error, 'response.data.message') ||
      'Oops, something went wrong. Please ask for help in #trident-questions Slack channel';
  }
  snackBar.error({ content: message });
}

export const API_REQUEST = Symbol('API_REQUEST');
export const CHECK_NETWORK = Symbol('CHECK_NETWORK');
export const REQUEST = '_REQUEST';
export const SUCCESS = '_SUCCESS';
export const FAILURE = '_FAILURE';
export const SERVICE_NAME = 'serviceName';

export default (store) => (next) => (action) => {
  if (_.get(action, 'meta.requestStatus') === FAILURE) {
    // Show error for actions not created from this middleware
    showError(_.get(action, 'payload'), {});
  }
  if (!_.get(action, ['meta', API_REQUEST])) {
    // Non-api request action
    return next(action);
  }

  // type     is the base action type that will trigger
  // payload  is the request body to be processed
  const { type, payload, meta } = action;

  // Swap the action content and structured api results
  function constructActionWith(data) {
    const finalAction = { ...action, ...data };
    delete finalAction[API_REQUEST];
    return finalAction;
  }

  const requestTime = new Date();

  // Propagate the start of the request.
  next(
    constructActionWith({
      type: type + REQUEST,
      payload,
      meta: {
        ...meta,
        requestStatus: REQUEST,
        requestTime,
      },
    }),
  );

  // Get the access token and config from store.
  const state = store.getState();
  const accessToken = _.get(state, 'app.user.accessToken');
  const version = _.get(state, 'app.config.version');
  const metaServiceName = _.get(action, ['meta', SERVICE_NAME]);
  const baseUrl =
    (metaServiceName &&
      window.Grab.config.getServiceBaseUrl(metaServiceName)) ||
    window.Grab.config.getApiBaseUrl(version);
  const authHeader = 'Authentication';

  let promise;
  if (action.meta.batch) {
    promise = makeBatchRequests(
      payload,
      accessToken,
      baseUrl,
      authHeader,
      concedoAuthHeader,
    );
  } else {
    promise = makeRequest(
      payload,
      accessToken,
      baseUrl,
      authHeader,
      concedoAuthHeader,
      metaServiceName === SERVICE_NAMES.HEDWIG, // append hedwig header
    );
  }

  function defaultErrorHandler(error, next, action) {
    const checkNetwork = action.meta && action.meta[CHECK_NETWORK];
    showError(error, { checkNetwork });
    next(
      constructActionWith({
        type: action.type + FAILURE,
        payload: error,
        meta: {
          ...meta,
          requestStatus: FAILURE,
          request: action.payload,
          requestTime,
        },
      }),
    );
  }

  // Propagate the response of the request.
  return promise
    .then((response) => {
      next(
        constructActionWith({
          type: type + SUCCESS,
          payload: _.isArray(response)
            ? _.map(response, (res) => res.data)
            : response.data,
          meta: {
            ...meta,
            requestStatus: SUCCESS,
            request: payload,
            requestTime,
          },
        }),
      );
    })
    .catch((error) => {
      const responseCode = _.get(error, 'response.status', 0);
      // sending non 400 error codes to sentry for tracking
      if (responseCode < 400 || responseCode > 499) {
        const errData = _.get(error, 'response.data', {});
        Sentry.captureException(errData, 'error', {
          type: 'api',
          result: 'fail',
        });
      }
      if (action.meta.errorHandler) {
        return action.meta.errorHandler(
          error,
          next,
          action,
          defaultErrorHandler,
        );
      }
      defaultErrorHandler(error, next, action);
    });
};
